import { useState, useRef, useEffect, useCallback } from 'react'
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { sileo } from 'sileo'
import { useApp } from '../../context/AppContext'
import useUser from '../../hooks/useUser'
import useNotifications from '../../hooks/useNotifications'
import useTheme from '../../hooks/useTheme'
import AuthAccessValidator from '../../components/Auth/AuthAccessValidator'
import ticketService from '../../services/ticketService'
import { employeeName } from '../../utils/employee'
import GmLauncher from './GmLauncher'

/**
 * Barra superior con el shell del Design System (SiteFlow), el mismo que usan
 * Proyectos, Operaciones, Talento Humano y Documentos: 60 px de alto y tres
 * zonas — izquierda (hamburguesa + breadcrumb), centro (búsqueda global) y
 * derecha (launcher de sistemas, ayuda, campana y avatar).
 *
 * Las clases (.cms-topbar, .topbar-breadcrumb, .topbar-icon-btn, .user-dropdown…)
 * salen de styles/gm-topbar.css, extraído del Design System.
 *
 * @component
 * @param {Object} props
 * @param {Function} props.toggleSidebar - Pliega la sidebar (o abre el cajón en móvil).
 * @returns {JSX.Element}
 */

/** Icono y color de cada tipo de notificación. */
const typeConfig = {
  warning: {
    icon: 'fa-triangle-exclamation',
    color: 'text-warning',
    bg: 'notif-warning',
  },
  info: { icon: 'fa-circle-info', color: 'text-info', bg: 'notif-info' },
  success: {
    icon: 'fa-circle-check',
    color: 'text-success',
    bg: 'notif-success',
  },
  error: { icon: 'fa-circle-xmark', color: 'text-danger', bg: 'notif-error' },
}

/**
 * Tiempo transcurrido en formato corto (Ahora, 5m, 2h, 12 sept).
 * Vive fuera del componente: lee el reloj, así que no debe correr en el render.
 * @param {string} dateStr
 * @returns {string}
 */
const timeAgo = (dateStr) => {
  const diff = Math.floor((Date.now() - new Date(dateStr)) / 1000)
  if (diff < 60) return 'Ahora'
  if (diff < 3600) return `${Math.floor(diff / 60)}m`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`
  return new Date(dateStr).toLocaleDateString('es', {
    day: '2-digit',
    month: 'short',
  })
}

/** Breadcrumb de cada pantalla: ['Sección', 'Pantalla'] o ['Pantalla']. */
const RUTAS = {
  '/': ['Inicio'],
  '/nuevo-ticket': ['Tickets', 'Nuevo ticket'],
  '/tickets': ['Tickets', 'Todos los tickets'],
  '/mis-tickets': ['Tickets', 'Mis tickets'],
  '/reportes': ['Tickets', 'Reportes'],
  '/reportar-incidente': ['Seguridad', 'Reportar incidente'],
  '/incidentes': ['Seguridad', 'Incidentes'],
  '/soluciones': ['Recursos', 'Soluciones'],
  '/ayuda': ['Recursos', 'Centro de ayuda'],
  '/configuracion': ['Sistema', 'Configuración'],
  '/mi-cuenta': ['Mi cuenta'],
}

const Topbar = ({ toggleSidebar }) => {
  const { user, employee: currentEmployee, logout } = useApp()
  const { employee } = useUser(user?.id, 'Auth')
  const { pathname } = useLocation()
  const params = useParams()
  const navigate = useNavigate()
  const { dark, toggle: toggleTheme } = useTheme()
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    loading: loadingNotif,
  } = useNotifications()

  const [search, setSearch] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 })
  const inputRef = useRef(null)

  const notifBtnRef = useRef(null)
  const [showNotifications, setShowNotifications] = useState(false)
  const [notifPos, setNotifPos] = useState({ top: 0, left: 0 })

  const updatePosition = useCallback(() => {
    const rect = inputRef.current?.getBoundingClientRect()
    if (rect) {
      setPos({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      })
    }
  }, [])

  const updateNotifPosition = () => {
    const rect = notifBtnRef.current?.getBoundingClientRect()
    if (rect) {
      setNotifPos({
        top: rect.bottom + window.scrollY,
        left: Math.max(12, rect.right + window.scrollX - 320),
      })
    }
  }

  useEffect(() => {
    if (!open) return
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [open, updatePosition])

  useEffect(() => {
    if (!showNotifications) return
    const cerrar = () => setShowNotifications(false)
    window.addEventListener('click', cerrar)
    return () => window.removeEventListener('click', cerrar)
  }, [showNotifications])

  // Ctrl+K lleva al buscador, igual que en las demás apps.
  useEffect(() => {
    const atajo = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        inputRef.current?.querySelector('input')?.focus()
      }
    }
    window.addEventListener('keydown', atajo)
    return () => window.removeEventListener('keydown', atajo)
  }, [])

  /**
   * Busca tickets por número o por título.
   * @param {string} value
   */
  async function searchTickets(value) {
    if (!value.trim()) {
      setResults([])
      setOpen(false)
      return
    }
    setLoading(true)
    setOpen(true)
    updatePosition()
    const { data } = await ticketService.search(value, 10)
    setResults(data || [])
    setLoading(false)
  }

  const cerrarSesion = async () => {
    try {
      await logout()
      navigate('/login')
    } catch (err) {
      console.error('Error cerrando sesión:', err)
      sileo.error({ title: 'No se pudo cerrar sesión' })
    }
  }

  const migas =
    RUTAS[pathname] ??
    (pathname.startsWith('/ticket/')
      ? ['Tickets', `Ticket #${params.id ?? ''}`]
      : ['Tickets'])
  const seccion = migas.length > 1 ? migas[0] : null
  const actual = migas[migas.length - 1]

  // Sólo el nombre de la persona; nunca el correo.
  const name =
    employeeName(employee) ||
    employeeName(currentEmployee) ||
    user?.user_metadata?.name ||
    'Usuario'
  const role = (user?.user_metadata?.role || 'Usuario').toUpperCase()
  const inicial = name.trim().charAt(0).toUpperCase() || 'U'

  const avatar = (clase) =>
    employee?.photo ? (
      <div
        className={clase}
        style={{ background: 'none', padding: 0, overflow: 'hidden' }}
      >
        <img
          src={employee.photo}
          alt=""
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: 'inherit',
          }}
        />
      </div>
    ) : (
      <div className={clase}>{inicial}</div>
    )

  return (
    <header className="cms-topbar">
      {/* Izquierda: hamburguesa + breadcrumb */}
      <div className="topbar-left">
        <button
          className="topbar-toggle"
          onClick={toggleSidebar}
          title="Mostrar u ocultar el menú"
          aria-label="Mostrar u ocultar el menú"
        >
          <i className="fas fa-bars"></i>
        </button>
        <nav className="topbar-breadcrumb" aria-label="breadcrumb">
          <Link to="/" className="bc-link">
            Tickets
          </Link>
          <i className="fas fa-chevron-right bc-sep"></i>
          {seccion && (
            <>
              <span className="bc-link bc-section">{seccion}</span>
              <i className="fas fa-chevron-right bc-sep"></i>
            </>
          )}
          <span className="bc-current">{actual}</span>
        </nav>
      </div>

      {/* Centro: búsqueda global (sólo soporte) */}
      <div className="topbar-search" ref={inputRef}>
        <AuthAccessValidator AccessNames={['ticket_support']} showError={false}>
          {/* Mismo buscador que las demás apps: clases .gsearch del shell. */}
          <div className="gsearch">
            <i className="fas fa-magnifying-glass gsearch-icon"></i>
            <input
              type="text"
              className="gsearch-input"
              placeholder="Buscar ticket…"
              autoComplete="off"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                searchTickets(e.target.value)
              }}
            />
            {search && (
              <button
                type="button"
                className="gsearch-clear"
                aria-label="Limpiar búsqueda"
                onClick={() => {
                  setSearch('')
                  setResults([])
                  setOpen(false)
                }}
              >
                <i className="fas fa-xmark"></i>
              </button>
            )}
          </div>
        </AuthAccessValidator>
      </div>

      {/* Derecha: launcher · ayuda · campana · usuario */}
      <div className="topbar-right">
        <GmLauncher />

        <button
          className="topbar-icon-btn"
          type="button"
          data-bs-toggle="offcanvas"
          data-bs-target="#helpPanel"
          aria-controls="helpPanel"
          title="Ayuda de esta pantalla"
          aria-label="Ayuda de esta pantalla"
        >
          <i className="fas fa-circle-question"></i>
        </button>

        <button
          ref={notifBtnRef}
          className="topbar-icon-btn"
          title="Notificaciones"
          aria-label="Notificaciones"
          onClick={(e) => {
            e.stopPropagation()
            updateNotifPosition()
            setShowNotifications((v) => !v)
          }}
        >
          <i className="fas fa-bell"></i>
          {unreadCount > 0 && (
            <span className="notif-badge">{unreadCount}</span>
          )}
        </button>

        <div className="dropdown">
          <button
            className="topbar-avatar-btn"
            id="userMenuBtn"
            data-bs-toggle="dropdown"
            data-bs-display="static"
            data-bs-auto-close="outside"
            aria-expanded="false"
            aria-label={name}
          >
            {avatar('topbar-avatar')}
          </button>
          <ul
            className="dropdown-menu dropdown-menu-end user-dropdown"
            aria-labelledby="userMenuBtn"
          >
            <li className="user-dropdown-header">
              {avatar('ud-avatar')}
              <div>
                <div className="ud-name">{name}</div>
                <div className="ud-role">{role}</div>
              </div>
            </li>
            <li>
              <hr className="dropdown-divider my-1" />
            </li>
            <li>
              <button
                type="button"
                className="theme-toggle"
                role="switch"
                aria-checked={dark}
                onClick={toggleTheme}
              >
                <i className={`fas ${dark ? 'fa-sun' : 'fa-moon'}`}></i>
                <span className="theme-toggle-label">
                  {dark ? 'Modo claro' : 'Modo oscuro'}
                </span>
                <span className="theme-switch" aria-hidden="true"></span>
              </button>
            </li>
            <li>
              <hr className="dropdown-divider my-1" />
            </li>
            <li>
              <Link className="dropdown-item" to="/mi-cuenta">
                <i className="fas fa-user"></i> Mi cuenta
              </Link>
            </li>
            <li>
              <hr className="dropdown-divider my-1" />
            </li>
            <li>
              <button
                className="dropdown-item text-danger"
                onClick={cerrarSesion}
              >
                <i className="fas fa-arrow-right-from-bracket"></i> Cerrar
                sesión
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* Resultados de la búsqueda (portal: se sale del topbar) */}
      {open &&
        search &&
        createPortal(
          <div
            style={{
              position: 'absolute',
              top: pos.top + 8,
              left: pos.left,
              width: pos.width,
              zIndex: 999999,
            }}
            className="gm-search-results"
          >
            {loading ? (
              <div
                className="p-3 small text-center"
                style={{ color: 'var(--text-muted)' }}
              >
                Buscando…
              </div>
            ) : results.length === 0 ? (
              <div
                className="p-3 small text-center"
                style={{ color: 'var(--text-muted)' }}
              >
                Sin resultados
              </div>
            ) : (
              results.map((t) => (
                <Link
                  key={t.id}
                  to={`/ticket/${t.id}`}
                  onClick={() => setOpen(false)}
                >
                  #{t.id} — {t.title}
                </Link>
              ))
            )}
          </div>,
          document.body
        )}

      {/* Notificaciones (portal) */}
      {showNotifications &&
        createPortal(
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: notifPos.top + 8,
              left: notifPos.left,
              zIndex: 999999,
            }}
            className="gm-notif-panel"
          >
            <div className="notif-header">
              <span>Notificaciones</span>
              {unreadCount > 0 && (
                <button
                  className="btn btn-link p-0 text-decoration-none notif-mark-all-btn"
                  onClick={markAllAsRead}
                  title="Marcar todas como leídas"
                >
                  <i className="fa-solid fa-check-double me-1"></i>Marcar todas
                </button>
              )}
            </div>

            <div style={{ maxHeight: 350, overflowY: 'auto' }}>
              {loadingNotif ? (
                <div className="notif-empty">Cargando…</div>
              ) : notifications.length === 0 ? (
                <div className="notif-empty">
                  <i className="fas fa-bell-slash d-block mb-2 fs-5"></i>
                  No hay notificaciones
                </div>
              ) : (
                <AnimatePresence initial={false}>
                  {notifications.map((n) => {
                    const { icon, color, bg } =
                      typeConfig[n.type] ?? typeConfig.info
                    return (
                      <motion.div
                        key={n.id}
                        initial={{ opacity: 0, height: 0, y: -8 }}
                        animate={{ opacity: 1, height: 'auto', y: 0 }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        className={`notif-item ${!n.readAt ? `notif-unread ${bg}` : ''}`}
                        onClick={() => {
                          if (!n.readAt) markAsRead(n.id)
                        }}
                      >
                        {!n.readAt && <div className="notif-bar" />}
                        <div className="d-flex gap-3 align-items-start w-100">
                          <div className={`notif-icon-wrap ${color}`}>
                            <i className={`fa-solid ${icon}`}></i>
                          </div>
                          <div className="flex-grow-1 min-w-0">
                            <div className="d-flex justify-content-between align-items-center gap-2">
                              <span
                                className={`small fw-semibold text-truncate ${!n.readAt ? 'notif-title-unread' : ''}`}
                              >
                                {n.title}
                              </span>
                              <small className="flex-shrink-0 notif-time">
                                {timeAgo(n.createdAt)}
                              </small>
                            </div>
                            <p
                              className="mb-0 small lh-sm mt-1"
                              style={{ color: 'var(--text-2)' }}
                            >
                              {n.body}
                            </p>
                            {n.entityType === 'ticket' && n.entityId && (
                              <Link
                                to={`/ticket/${n.entityId}`}
                                className="notif-link mt-2 d-inline-flex align-items-center gap-1"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <i className="fa-solid fa-ticket fa-xs"></i> Ver
                                ticket #{n.entityId}
                              </Link>
                            )}
                          </div>
                          {!n.readAt && (
                            <div className="notif-dot flex-shrink-0" />
                          )}
                        </div>
                      </motion.div>
                    )
                  })}
                </AnimatePresence>
              )}
            </div>
          </motion.div>,
          document.body
        )}
    </header>
  )
}

export default Topbar
